import {
  Button,
  FormikForm,
  FormikSubmitButton,
  FormikTextArea,
  FormikTextField,
  PageContainer,
  ResponsiveButtonBox,
  Spacer,
  Text,
  Title,
  useToast,
} from '@color/continuum';
import {
  logAnalyticsPageView,
  trackAnalyticsEvent,
  useUrlQueryParam,
  ZendeskTag,
} from '@color/lib';
import SendIcon from '@mui/icons-material/Send';
import { Formik } from 'formik';
import { generatePath, useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { useActionItemQuery } from 'ui/common/apis/actionItems/api';

import { useCreateConversationMutation } from '../apis/api';
import { ICreateConversationPayload } from '../apis/types';
import { ACTION_ITEM_SUBTYPE_TO_CONVERSATION_TAGS } from '../constants';
import { PATIENT_MESSAGING_CONVERSATION_DETAIL_PATH } from '../paths';

export const ConversationCreateView = () => {
  const history = useHistory();
  const { showErrorToast } = useToast();
  const { queryParam: actionItemId } = useUrlQueryParam('action_id');
  const { queryParam: defaultSubject } = useUrlQueryParam('subject');
  const { queryParam: defaultMessage } = useUrlQueryParam('message');
  const { data: actionItem } = useActionItemQuery(actionItemId);
  const { mutateAsync: createConversation, isPending: isLoading } = useCreateConversationMutation(
    (conversationId: string) =>
      history.push(
        generatePath(PATIENT_MESSAGING_CONVERSATION_DETAIL_PATH, {
          conversationId,
        })
      ),
    () => showErrorToast('Error creating conversation.')
  );

  logAnalyticsPageView('messaging - patient-dashboard - new');

  // TODO (TELE-1794): Introduce translate-able strings for all of the hardcoded content here.
  return (
    <PageContainer helmetTitle="Conversation Create" maxWidth="sm">
      <Title>New Message</Title>
      <Spacer variant="medium" />
      <Text>This will be sent to your care team.</Text>
      <Spacer variant="small" />
      <Text>Responses may take 24 hours. If this is a medical emergency, please call 911.</Text>
      <Spacer variant="medium" />
      <Formik<ICreateConversationPayload>
        initialValues={{
          subject: defaultSubject || '',
          message: defaultMessage || '',
          tags: [],
        }}
        validationSchema={Yup.object({
          subject: Yup.string()
            .required('Subject is required.')
            .max(100, 'Subject must be less than 100 characters.'),
          message: Yup.string()
            .required('Message is required.')
            .max(4096, 'Message must be less than 4096 characters.'),
        })}
        onSubmit={async (formikValues: ICreateConversationPayload) => {
          trackAnalyticsEvent('messaging - patient-dashboard - new - submit clicked');

          const actionItemTags =
            (actionItem && ACTION_ITEM_SUBTYPE_TO_CONVERSATION_TAGS.get(actionItem.sub_type)) || [];
          const tags = new Set([ZendeskTag.PATIENT_INITIATED, ...actionItemTags]);
          await createConversation({ ...formikValues, tags: Array.from(tags) });
        }}
      >
        <FormikForm>
          <FormikTextField name="subject" type="text" label="Subject" required />
          <Spacer variant="medium" />
          <FormikTextArea label="Message" name="message" required maxRows={10} />
          <Spacer variant="medium" />
          <ResponsiveButtonBox shouldRenderCtaLeftToRight>
            <FormikSubmitButton
              startIcon={<SendIcon />}
              errorMessageIsVisible={false}
              isLoading={isLoading}
            >
              Send Message
            </FormikSubmitButton>
            <Button
              onClick={() => {
                trackAnalyticsEvent('messaging - patient-dashboard - new - cancel clicked');
                history.goBack();
              }}
              variant="color-secondary"
            >
              Cancel
            </Button>
          </ResponsiveButtonBox>
        </FormikForm>
      </Formik>
    </PageContainer>
  );
};
